import { useState, useEffect } from 'react';
import AlertModal from './components/AlertModal';

// ─── Field Definitions ────────────────────────────────────────────────────────

// const COMPANY_FIELDS = [
//   { id: 'partyname', label: 'Party Name', type: 'text', placeholder: 'Registered business / party name', required: true },
//   { id: 'tin', label: 'TIN', type: 'text', placeholder: 'Tax Identification Number', required: true },
//   { id: 'email', label: 'Email Address', type: 'email', placeholder: 'company@example.com', required: true },
//   { id: 'telephone', label: 'Telephone', type: 'tel', placeholder: '+234 000 000 0000', required: true },
//   { id: 'businessdesc', label: 'Business Description', type: 'text', placeholder: 'Brief description of your business', required: false },
//   { id: 'streetname', label: 'Street Name', type: 'text', placeholder: '12 Commerce Road', required: true },
//   { id: 'cityname', label: 'City Name', type: 'text', placeholder: 'Lagos', required: true },
//   { id: 'postalzone', label: 'Postal Zone', type: 'text', placeholder: '100001', required: false },
//   { id: 'country', label: 'Country', type: 'text', placeholder: 'Nigeria', required: true },
// ];

const API_FIELDS = [
  { id: 'baseurl', label: 'Base URL', type: 'text', placeholder: 'https://api.firs.gov.ng', required: true, large: false },
  { id: 'clientid', label: 'API Key', type: 'text', placeholder: 'Your API Client ID', required: true, large: false },
  { id: 'clientsecretkey', label: 'Client Secret Key', type: 'password', placeholder: 'Your Client Secret Key', required: true, large: false },
  { id: 'certificate', label: 'Certificate', type: 'text', placeholder: 'Paste your Certificate here…', required: true, large: false, rows: 1 },
  { id: 'publickey', label: 'Public Key', type: 'textarea', placeholder: 'Paste your Public Key here…', required: true, large: true, rows: 4 },
  { id: 'businessid', label: 'Business ID', type: 'text', placeholder: 'FIRS Business ID', required: true, large: false },
  { id: 'irncode', label: 'IRN Template Key', type: 'text', placeholder: 'Invoice Reference Number Code', required: true, large: true },
];

const RECORD_FIELDS = [
  { id: 'record_partyname', label: 'Registered Business Name', type: 'text', placeholder: 'Registered business name', required: true },
  { id: 'record_tin', label: 'TIN', type: 'text', placeholder: 'Tax Identification Number', required: true },
  { id: 'record_email', label: 'Email Address', type: 'email', placeholder: 'company@example.com', required: true },
  { id: 'record_telephone', label: 'Telephone', type: 'tel', placeholder: '+234 000 000 0000', required: true },
  { id: 'record_businessdesc', label: 'Business Description', type: 'text', placeholder: 'Brief description of your business', required: false },
  { id: 'record_streetname', label: 'Street Name', type: 'text', placeholder: '12 Commerce Road', required: true },
  { id: 'record_cityname', label: 'City Name', type: 'text', placeholder: 'Lagos', required: true },
  { id: 'record_postalzone', label: 'Postal Zone', type: 'text', placeholder: '100001', required: false },
  { id: 'record_country', label: 'Country', type: 'text', placeholder: 'Nigeria', required: true },
];

// ─── Initial State ─────────────────────────────────────────────────────────────

const buildInitialState = (fields) =>
  fields.reduce((acc, f) => ({ ...acc, [f.id]: '' }), {});

// ─── Reusable Field Component ─────────────────────────────────────────────────

function FormField({ field, value, onChange }) {
  const baseClass =
    'w-full px-4 py-2.5 bg-gray-900/70 text-white rounded-lg border border-gray-600 ' +
    'focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 focus:outline-none ' +
    'transition-all duration-200 placeholder-gray-500 text-sm';

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={field.id} className="text-sm font-medium text-gray-300 flex items-center gap-1">
        {field.label}
        {field.required && <span className="text-red-400 text-xs">*</span>}
      </label>

      {field.type === 'textarea' ? (
        <textarea
          id={field.id}
          value={value}
          onChange={onChange}
          rows={field.rows || 4}
          required={field.required}
          placeholder={field.placeholder}
          className={`${baseClass} resize-none font-mono text-xs`}
        />
      ) : (
        <input
          id={field.id}
          type={field.type}
          value={value}
          onChange={onChange}
          required={field.required}
          placeholder={field.placeholder}
          className={baseClass}
        />
      )}
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────

function SectionCard({ icon, title, subtitle, accent, children }) {
  return (
    <div className={`rounded-xl border ${accent} bg-gray-800/50 backdrop-blur-sm overflow-hidden`}>
      {/* Card Header */}
      <div className="px-6 py-4 border-b border-gray-700/60 flex items-center gap-3">
        <div className={`text-2xl`}>{icon}</div>
        <div>
          <h3 className="text-white font-semibold text-base leading-tight">{title}</h3>
          <p className="text-gray-400 text-xs mt-0.5">{subtitle}</p>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-6">{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function Setup() {
  const [showSection1, setShowSection1] = useState(true);
  const [showSection2, setShowSection2] = useState(false);
  const [loading, setLoading] = useState(false);

  //const [companyData, setCompanyData] = useState(buildInitialState(COMPANY_FIELDS));
  const [apiData, setApiData] = useState(buildInitialState(API_FIELDS));
  const [recordData, setRecordData] = useState(buildInitialState(RECORD_FIELDS));

  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // ── Load existing data on mount ────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { fnGetBusinessData } = await import('./serverjs/customFieldsNew.js');
        const existing = await fnGetBusinessData();
        if (existing) {
          // Pre-fill Company section
          // setCompanyData({
          //   partyname: existing.partyname,
          //   tin: existing.tin,
          //   email: existing.email,
          //   telephone: existing.telephone,
          //   businessdesc: existing.businessdesc,
          //   streetname: existing.streetname,
          //   cityname: existing.cityname,
          //   postalzone: existing.postalzone,
          //   country: existing.country,
          // });
          // Pre-fill API section
          setApiData({
            baseurl:         existing.baseurl         ?? '',
            clientid:        existing.clientid        ?? '',
            clientsecretkey: existing.clientsecretkey ?? '',
            certificate:     existing.certificate     ?? '',
            publickey:       existing.publickey       ?? '',
            businessid:      existing.businessid      ?? '',
            irncode:         existing.irncode         ?? '',
          });
          // Pre-fill Record section
          setRecordData({
            record_partyname:    existing.record_partyname    ?? '',
            record_tin:          existing.record_tin          ?? '',
            record_email:        existing.record_email        ?? '',
            record_telephone:    existing.record_telephone    ?? '',
            record_businessdesc: existing.record_businessdesc ?? '',
            record_streetname:   existing.record_streetname   ?? '',
            record_cityname:     existing.record_cityname     ?? '',
            record_postalzone:   existing.record_postalzone   ?? '',
            record_country:      existing.record_country      ?? '',
          });
          // Skip welcome screen — custom fields already set up
          setShowSection1(false);
          setShowSection2(true);
        }
      } catch (err) {
        console.error('Error loading existing business data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Alert Helpers ────────────────────────────────────────────────────────────

  const showAlert = (message, type = 'info', title = '') => {
    const titles = {
      success: '✅ Success',
      error: '❌ Error',
      warning: '⚠️ Warning',
      info: 'ℹ️ Information',
    };
    setAlert({
      isOpen: true,
      type,
      title: title || titles[type],
      message,
    });
  };

  const closeAlert = () => {
    setAlert({ ...alert, isOpen: false });
  };

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleChange = (setter) => (e) => {
    const { id, value } = e.target;
    setter((prev) => ({ ...prev, [id]: value }));
  };

  const handleCompleteSetup = async () => {
    setLoading(true);
    try {
      const { IsAllCustomfieldsValid } = await import('./serverjs/customFieldsNew.js');
      const result = await IsAllCustomfieldsValid();
      if (result === true) {
        setShowSection1(false);
        setShowSection2(true);
      } else {
        showAlert('Custom field creation failed. Please check the console for details.', 'error');
      }
    } catch (error) {
      console.error('Setup error:', error);
      showAlert('An error occurred during setup. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestPost = async () => {
    setLoading(true);
    try {
      const { managerApi } = await import('./serverjs/jofotaraFuncs.js');
      const payload = {
        Name: "CLIENT ID [FIRS]",
        Placement: ["38cf4712-6e95-4ce1-b53a-bff03edad273"],
        Size: 2,
        Description: "Registered FIRS Client ID [DON'T DELETE]",
        Key: "041724b7-aeae-42ad-8ed7-7b037478866b"
      };
      console.log('🧪 Test POST payload:', payload);
      const response = await managerApi('POST', '/api2/text-custom-field-form', payload);
      console.log('🧪 Test POST response:', response);
      showAlert(`Test POST done. Status: ${response?.status ?? 'unknown'}`, 'success', '🧪 Test POST');
    } catch (error) {
      console.error('Test POST error:', error);
      showAlert('Test POST failed. Check the console for details.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { fnPostBusinessData } = await import('./serverjs/customFieldsNew.js');
      const result = await fnPostBusinessData({  ...apiData, ...recordData });
      //const result = await fnPostBusinessData({ ...companyData, ...apiData, ...recordData });
      if (result === true) {
        showAlert('Configuration saved successfully.', 'success');
        window.location.href = '/';
      } else {
        showAlert('Failed to save configuration. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Save error:', error);
      showAlert('Failed to save configuration. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleV4Test21 = async () => {
    //setLoading(true);
    try {
      const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

      // const response = await managerApi('GET', '/api2/customers',null);
      // console.log('🧪 Test Businesses response:', response);

      // const value = {
      //   name: 'MyExtension',
      //   source: 1,
      //   endpoint: 'localhost:3000',
      //   html: true,
      //   placement: 'sales-invoice-view'
      // };
      // const business = "FASHION CHOISE(07.10.2025)"

      // const payload = {
      //   value,business
      // }
      // console.log('🧪 Test POST payload:', payload);
      // const response = await managerApi('POST', '/api4/extension', payload);
      // console.log('🧪 Test POST response:', response?.body);

      // const value = {
      //   name: 'MyExtension2',
      //   source: 1,
      //   placement: 'sales-invoice-view',
      //   key: '72e778ab-bb54-4679-a0e7-b4e0e00a6751'
      // };
      // const business = "FASHION CHOISE(07.10.2025)"

      // const payload = {
      //   value,business
      // }
      // console.log('🧪 Test POST payload:', payload);
      // const response = await managerApi('POST', '/api4/extension', payload);
      //console.log('🧪 Test POST response:', response);
     //var urlCF = '/api4/extension?key=' + encodeURIComponent(p.key)
     var urlEx =   '/api4/extension-batch?Skip=0&PageSize=100&business=' + encodeURIComponent('FASHION CHOISE(07.10.2025)');
     var urlCF =   '/api4/custom-fields?business=' + encodeURIComponent('FASHION CHOISE(07.10.2025)');
     var urlText = '/api4/text-custom-field-batch?q=ogYaRkFTSElPTiBDSE9JU0UoMDcuMTAuMjAyNSk' 
     const response = await managerApi('GET', urlEx,null);
     console.log('🧪 Test Businesses response:', response);
      
    // const businesses = response?.body?.businesses ?? [];
    // if (businesses.length === 0) {
    //   showAlert('No businesses found.', 'info', '🧪 GET Businesses');
    // } else {
    //   const names = businesses.map((b) => `• ${b.name} (${b.key})`).join('\n');
    //   showAlert(`Found ${businesses.length} business(es):\n${names}`, 'success', '🧪 GET Businesses');
    // }

    } catch (error) {
      console.error('Test POST error:', error);
     
    } finally {
      //setLoading(false);
    }
  };

  const deleteAPI = async() => {
    try {
      const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

      const response = await managerApi('DELETE', '/api4/extension?Key=019e9691-c7dc-7223-8c10-5abaa49163ec&Business=FASHION CHOISE(07.10.2025)',null);
     console.log('🧪 Test Businesses response:', response);


     } catch (error) {
      console.error('Test POST error:', error);
     
    } finally {
      //setLoading(false);
    }  
  }

   const getExtensionsAPI = async() => {
    try {
      const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

      const response = await managerApi('GET', '/api4/extension-batch?Skip=0&PageSize=100',null);
     console.log('🧪 Test Businesses response:', response);


     } catch (error) {
      console.error('Test POST error:', error);
     
    } finally {
      //setLoading(false);
    }  
  }

   const putExtensionsAPI = async() => {
    try {
      
      const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

      const payloadInv = {
      key: '6406c312-b3b5-42d9-b072-f9ea08e3e505',
      value: {
        name: 'FIRS_Invoice',        // Display name in Manager
        source: 0,                      // 1 = Endpoint (iframe), 2 = HTML
        endpoint: window.location.origin, // Extension URL — resolves to the deployed origin
        placement: 'sales-invoice-view',  // Where it appears in Manager UI
        inactive: false,
        key:'6406c312-b3b5-42d9-b072-f9ea08e3e505'
      },
      business: 'WELCOME STORE FIRS'
      // business: 'YOUR BUSINESS NAME', // omit if running inside Manager iframe
    };
   
    const responseInv = await managerApi('PUT', '/api4/extension', payloadInv);
    console.log('🧪 PUT /api4/extension response:', responseInv);

    const payloadCrN = {
      key: 'f21e445e-3620-4ffa-96e5-7c8639ee7c8d',
      value: {
        name: 'FIRS_CreditNote',        // Display name in Manager
        source: 0,                      // 1 = Endpoint (iframe), 2 = HTML
        endpoint: window.location.origin, // Extension URL — resolves to the deployed origin
        placement: 'credit-note-view',  // Where it appears in Manager UI
        inactive: false,
        key:'f21e445e-3620-4ffa-96e5-7c8639ee7c8d'
      },
      business: 'WELCOME STORE FIRS'
      // business: 'YOUR BUSINESS NAME', // omit if running inside Manager iframe
    };

    
    const responseCrN = await managerApi('PUT', '/api4/extension', payloadCrN);
    console.log('🧪 PUT /api4/extension response:', responseCrN);

     } catch (error) {
      console.error('Test POST error:', error);
     
    } finally {
      //setLoading(false);
    }  
  }

  const TestV2API = async() => {
    try {
      const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

      const response = await managerApi('GET', '/api2/customers',null);
     console.log('🧪 Test Businesses response:', response);


     } catch (error) {
      console.error('Test POST error:', error);
     
    } finally {
      //setLoading(false);
    }  
  }

  const handleV4Test2 = async () => {
  setLoading(true);
  try {
    const { managerApi } = await import('./serverjs/jofotaraFuncs.js');

    // Generate a new UUID for the extension key (or use a fixed one to update existing)
    const extensionKey = crypto.randomUUID();

    const payload = {
      key: extensionKey,
      value: {
        name: 'FIRS e-Invoice-2',        // Display name in Manager
        source: 1,                      // 1 = Endpoint (iframe), 2 = HTML
        endpoint: '', // Your extension URL
        placement: 'sales-invoice-view',  // Where it appears in Manager UI
        inactive: false,
        key:'6406c312-b3b5-42d9-b072-f9ea08e3e505'
      },
      business: 'FASHION CHOISE(07.10.2025)'
      // business: 'YOUR BUSINESS NAME', // omit if running inside Manager iframe
    };

    console.log('🧪 PUT /api4/extension payload:', payload);
    const response = await managerApi('PUT', '/api4/extension', payload);
    console.log('🧪 PUT /api4/extension response:', response);

    if (response?.body === true) {
      showAlert(`Extension created/updated.\nKey: ${extensionKey}`, 'success', '🧪 PUT Extension');
    } else {
      showAlert(
        `Failed: ${response?.body?.message ?? JSON.stringify(response?.body)}`,
        'error',
        '🧪 PUT Extension'
      );
    }
  } catch (error) {
    console.error('PUT extension error:', error);
    showAlert('PUT extension failed. Check the console for details.', 'error');
  } finally {
    setLoading(false);
  }
};

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-slate-900 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* ── Page Header ── */}
        <div className="text-center space-y-1 mb-2">
          <h1 className="text-3xl font-bold text-white tracking-tight">
            🇳🇬&nbsp; FIRS e-Invoice Setup
          </h1>
          <p className="text-gray-400 text-sm">Configure your company and API credentials</p>
        </div>

        {/* ══════════════════════════ SECTION 1 — WELCOME ══════════════════════════ */}
        {showSection1 && (
          <div className="rounded-xl border border-purple-500/30 bg-gray-800/50 backdrop-blur-sm shadow-2xl p-8 space-y-5">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🚀</span>
              <div>
                <h2 className="text-2xl font-bold text-white">Welcome to Setup</h2>
                <p className="text-gray-400 text-sm">First-time configuration wizard</p>
              </div>
            </div>

            <div className="space-y-3 text-gray-300 text-sm leading-relaxed border-l-2 border-purple-500/40 pl-4">
              <p>
                It appears this is your first visit. Please follow the instructional video to
                complete the setup process before proceeding.
              </p>
              <p>
                Clicking <strong className="text-white">Complete Setup</strong> will automatically
                create all required custom fields in Manager Accounting. Once done, you can enter
                your company and API credentials on the next screen.
              </p>
              <p className="text-amber-400/90">
                ⚠️ We recommend creating a backup of your production database before proceeding.
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleCompleteSetup}
                disabled={loading}
                className="inline-flex items-center gap-2 px-7 py-3 bg-purple-600 hover:bg-purple-500
                           disabled:bg-purple-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Processing…
                  </>
                ) : (
                  <>✅ Complete Setup</>
                )}
              </button>

              <button
                onClick={handleTestPost}
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-3 bg-sky-700 hover:bg-sky-600
                           disabled:bg-sky-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm border border-sky-500/40"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Posting…
                  </>
                ) : (
                  <>🧪 Test POST (CLIENT ID)</>
                )}
              </button>

              <button
                onClick={putExtensionsAPI} // TEST API
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-3 bg-sky-700 hover:bg-sky-600
                           disabled:bg-sky-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm border border-sky-500/40"
              >Test V4</button>
              <button
                onClick={deleteAPI} // TEST API
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-3 bg-sky-700 hover:bg-sky-600
                           disabled:bg-sky-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm border border-sky-500/40"
              >DELETE EXTENSION V4</button>

            </div>
          </div>
        )}

        {/* ══════════════════════════ SECTION 2 — FORM ══════════════════════════ */}
        {showSection2 && (
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>

            {/* ── Company Details Card ── */}
            {/* <SectionCard
              icon="🏢"
              title="Company Details"
              subtitle="Your registered business information as filed with FIRS"
              accent="border-cyan-500/30"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {COMPANY_FIELDS.map((field) => (
                  <div
                    key={field.id}
                    className={
                      field.id === 'businessdesc'
                        ? 'sm:col-span-2'   // full-width for description
                        : ''
                    }
                  >
                    <FormField
                      field={field}
                      value={companyData[field.id]}
                      onChange={handleChange(setCompanyData)}
                    />
                  </div>
                ))}
              </div>
            </SectionCard> */}

            {/* ── API Details Card ── */}
            <SectionCard
              icon="🔑"
              title="API Details"
              subtitle="FIRS integration credentials — keep these confidential"
              accent="border-amber-500/30"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {API_FIELDS.map((field) => (
                  <div
                    key={field.id}
                    className={field.large ? 'sm:col-span-2' : ''}
                  >
                    <FormField
                      field={field}
                      value={apiData[field.id]}
                      onChange={handleChange(setApiData)}
                    />
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── Record (Customer / Supplier) Details Card ── */}
            <SectionCard
              icon="👥"
              title="Complete Company Details"
              subtitle="Business record information for FIRS"
              accent="border-green-500/30"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {RECORD_FIELDS.map((field) => (
                  <div
                    key={field.id}
                    className={
                      field.id === 'record_businessdesc'
                        ? 'sm:col-span-2'
                        : ''
                    }
                  >
                    <FormField
                      field={field}
                      value={recordData[field.id]}
                      onChange={handleChange(setRecordData)}
                    />
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── Submit ── */}
            <div className="flex items-center justify-end gap-3 flex-wrap">
              {/* <button
                type="button"
                onClick={handleTestPost}
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-3 bg-sky-700 hover:bg-sky-600
                           disabled:bg-sky-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm border border-sky-500/40"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Posting…
                  </>
                ) : (
                  <>🧪 Test POST (CLIENT ID)</>
                )}
              </button> */}

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-600 hover:bg-emerald-500
                           disabled:bg-emerald-900 disabled:cursor-not-allowed text-white font-semibold
                           rounded-lg shadow-lg transition-all duration-200 hover:scale-105
                           disabled:hover:scale-100 text-sm"
              >
                {loading ? (
                  <>
                    <span className="animate-spin">⏳</span> Saving…
                  </>
                ) : (
                  <>💾 Save Configuration</>
                )}
              </button>
            </div>
          </form>
        )}

      </div>

      <AlertModal
        isOpen={alert.isOpen}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={closeAlert}
      />
    </div>
  );
}

export default Setup;
